import { useState } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Check, BookOpen, Users, Clock, Mail, ArrowRight, Shield } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface VerificationStatus {
  status: 'idle' | 'pending' | 'verified' | 'rejected';
  message?: string;
}

export default function StudentPlan() {
  const { theme } = useThemeStore();
  const { token, user, updateUser } = useAuthStore();
  const isDark = theme === 'dark';

  const [email, setEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({ status: 'idle' });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const benefits = [
    {
      icon: GraduationCap,
      title: 'Student Exclusive',
      description: 'Special pricing exclusively for verified students',
    },
    {
      icon: BookOpen,
      title: 'Research Assistance',
      description: 'AI employees to help with research papers and projects',
    },
    {
      icon: Users,
      title: 'Study Groups',
      description: 'Collaborate with classmates using shared AI assistants',
    },
    {
      icon: Clock,
      title: 'Extended Trial',
      description: '30-day free trial instead of the standard 7 days',
    },
  ];

  // Validate .edu email
  const validateEmail = (emailValue: string): boolean => {
    const eduPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.edu$/i;
    return eduPattern.test(emailValue);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid .edu email address');
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate .edu email
    if (!validateEmail(email)) {
      setErrorMessage('Invalid email. Only .edu email addresses are accepted for student verification.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${API_URL}/student/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          school_name: schoolName,
        }),
      });

      const data = await response.json() as { success: boolean; data?: { status: string }; error?: string };

      if (data.success) {
        setVerificationStatus({ status: 'verified', message: 'Your .edu email has been verified!' });
        setSuccessMessage('Your student email has been verified! You can now upgrade to the Student Plan.');

        // If the API returned a verified status, update the user
        if (data.data?.status === 'verified') {
          updateUser({ subscription_tier: 'student' });
        }
      } else {
        setErrorMessage(data.error || 'Failed to verify student email');
      }
    } catch (error) {
      console.error('Failed to submit verification:', error);
      setErrorMessage('Failed to verify student email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpgrade = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await fetch(`${API_URL}/student/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json() as { success: boolean; data?: { checkout_url: string }; error?: string };

      if (data.success && data.data?.checkout_url) {
        window.location.href = data.data.checkout_url;
      } else {
        setErrorMessage(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      setErrorMessage('Failed to create checkout session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStudentVerified = user?.subscription_tier === 'student' || verificationStatus.status === 'verified';

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="max-w-6xl mx-auto space-y-8 p-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-purple to-accent-pink mb-4">
          <GraduationCap className="w-8 h-8 text-white" />
        </div>
        <h1 className={`text-3xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Student Plan
        </h1>
        <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
          Exclusive pricing for students with a valid .edu email address. Access premium AI tools to help with your studies.
        </p>
      </motion.div>

      {/* Success/Error Messages */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-accent-green/20 border border-accent-green/30 text-accent-green px-4 py-3 rounded-xl text-center"
        >
          {successMessage}
        </motion.div>
      )}
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-center"
        >
          {errorMessage}
        </motion.div>
      )}

      {/* Pricing Card */}
      <motion.div variants={fadeInUp} className="flex justify-center">
        <div className={`rounded-3xl p-8 border-2 border-accent-purple relative max-w-md w-full ${isDark ? 'bg-dark-800' : 'bg-white shadow-xl'}`}>
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-gradient-to-r from-accent-purple to-accent-pink text-white text-sm font-medium px-4 py-1 rounded-full">
              Student Exclusive
            </span>
          </div>

          <div className="text-center mb-6">
            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Student Pro
            </h2>
            <div className="flex items-end justify-center gap-2">
              <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>$4.99</span>
              <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>/month</span>
            </div>
            <p className={`text-sm mt-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              Requires valid .edu email verification
            </p>
          </div>

          <ul className="space-y-3 mb-6">
            {[
              '8 AI Employees',
              '1,500 credits/month',
              'All integrations',
              'Email support',
              'Brain AI training',
              'Priority access to new features',
            ].map((feature) => (
              <li key={feature} className={`flex items-center text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                <Check className="w-5 h-5 text-accent-green mr-3 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          {isStudentVerified ? (
            <button
              onClick={handleUpgrade}
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {isSubmitting ? 'Processing...' : 'Upgrade to Student Pro'}
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <p className={`text-center text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              Verify your .edu email below to unlock this plan
            </p>
          )}
        </div>
      </motion.div>

      {/* Benefits */}
      <motion.div variants={fadeInUp}>
        <h2 className={`text-xl font-semibold mb-4 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Student Benefits
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className={`rounded-2xl p-6 border text-center ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
            >
              <div className="w-12 h-12 rounded-xl bg-accent-purple/10 flex items-center justify-center mx-auto mb-3">
                <benefit.icon className="w-6 h-6 text-accent-purple" />
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {benefit.title}
              </h3>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Verification Form */}
      {!isStudentVerified && (
        <motion.div variants={fadeInUp}>
          <div className={`rounded-2xl p-6 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-accent-cyan/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-accent-cyan" />
              </div>
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Verify Your Student Status
                </h2>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Only .edu email addresses are accepted for student verification
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  Academic Email (.edu required)
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="you@university.edu"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
                      emailError
                        ? 'border-red-500 focus:ring-red-500'
                        : isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted focus:ring-accent-purple'
                        : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-accent-purple'
                    } focus:ring-2 focus:border-transparent`}
                  />
                </div>
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
                <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Only .edu email addresses are accepted
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                  School / University Name
                </label>
                <input
                  type="text"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="Harvard University"
                  required
                  className={`w-full px-4 py-3 rounded-xl border ${
                    isDark
                      ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                  } focus:ring-2 focus:ring-accent-purple focus:border-transparent`}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !!emailError || !email || !schoolName}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Verifying...' : 'Verify Email'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </motion.div>
      )}

      {/* FAQ */}
      <motion.div variants={fadeInUp}>
        <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Frequently Asked Questions
        </h2>
        <div className="space-y-3">
          {[
            {
              q: 'What email addresses are accepted?',
              a: 'Only .edu email addresses from accredited educational institutions are accepted for the student plan.',
            },
            {
              q: 'How is verification done?',
              a: 'When you submit your .edu email, we automatically verify that it is a valid academic email address.',
            },
            {
              q: 'How long is the student plan valid?',
              a: 'The student plan is valid for 4 years or until you graduate, whichever comes first. You may need to re-verify annually.',
            },
            {
              q: 'Can I upgrade to a regular plan later?',
              a: 'Yes! You can upgrade to any regular plan at any time. Your student plan will be prorated.',
            },
          ].map((faq) => (
            <div
              key={faq.q}
              className={`rounded-xl p-4 border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
            >
              <h4 className={`font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{faq.q}</h4>
              <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{faq.a}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
